import { ClientInfoStatus, IClientInfo } from './client'
import { IEntityInfo } from './entityInfo'

export enum CheckDataStatus {
  New = 'NEW',
  Processed = 'PROCESSED',
  Deleted = 'DELETED',
  ProcessedDeleted = 'PROCESSED_DELETED',
  ProcessedDeletedSent = 'DELETED_SENT',
}

export enum ChecksUploadStage {
  Reading = 'reading',
  Predicting = 'predicting',
  Saving = 'saving',
}

/** Progress of one UploadChecks request, counted across all of its files. */
export interface IChecksUploadProgress {
  activityId: string
  stage: ChecksUploadStage
  /** Files read while `reading`, checks finished while `predicting`. */
  processed: number
  total: number
  fileCount: number
}

export enum CollectionType {
  Check = 'Check',
  Wire = 'Wire',
}

export interface ICheckDataPages {
  start: number
  end: number
  image: string
}

export interface ICheckData {
  data: ICheck[]
  totalCount: number
}

export interface ICheck {
  id: string
  checkNumber: string
  clientInfo?: IClientInfo
  checkAccount?: ICheckAccount
  accountNumber: string
  paymentAmount: number
  recordDate: string
  routingNumber: string
  lockbox?: number
  boxLink?: string
  pages?: ICheckDataPages
  status: CheckDataStatus
  originalEntity?: string
  clientName: string
  /** Jev's 0–1 score for the client it predicted; null once a person sets the client. */
  clientConfidence?: number | null
  hasInvoices?: boolean
}

export interface ICheckAccount {
  id: string
  accountNumber: string
  linkedName: string
  lastMappedDate?: string
  lastSeenDate?: string
  entityInfo?: IEntityInfo
}

export interface ILockboxReconciliation {
  lockbox: number
  total: number
  adjustments: number
}

export interface IChecksSummaryInfo {
  checksReconciliation: ILockboxReconciliation[]
  nonClientCashChecks: ICheck[]
  mappingProgress: number
}

export enum WireDataStatus {
  New = 'NEW',
  Processed = 'PROCESSED',
  Deleted = 'DELETED',
  ProcessedDeleted = 'PROCESSED_DELETED',
  ProcessedDeletedSent = 'DELETED_SENT',
  Duplicate = 'DUPLICATE',
}

export enum WirePredictionSource {
  /** Past memos from the same payer agree on an entity. */
  Precedent = 'precedent',
  /** The payer name closely matches an existing entity. */
  NameMatch = 'name_match',
  /** Atlas picked an existing entity from the closest candidates. */
  Atlas = 'atlas',
  /** No existing entity fits; the prediction is a new entity name. */
  NewEntity = 'new_entity',
}

export interface IClosestWireEntity {
  name: string
  /** Name similarity to the memo's payer, 0–1. */
  score: number
}

export interface IWirePredictionMetadata {
  source: WirePredictionSource
  /** 0–1: consensus share for a precedent, name similarity or Atlas confidence otherwise. */
  confidence: number | null
  /** Best existing entity, surfaced next to a new-entity prediction so it can be picked instead. */
  closestEntity: IClosestWireEntity | null
  predictedAt: string
}

export interface IWireData {
  id?: string
  clientName?: string
  recordDate: string
  paymentAmount: number
  accountNumber?: string
  debtor?: string
  memo?: string
  originalMemo?: string
  prediction?: string
  predictionEntityId?: string
  predictionMetadata?: IWirePredictionMetadata | null
  status?: WireDataStatus
  isPossibleMultipleClients?: boolean
  isChecked?: boolean
  fileId?: string
  activityId?: string
  totalViewCount?: number
  recType: 'checks' | 'wire'
  isReconciliationValue: boolean
  isPotentialDuplicate?: boolean
  isPotentialDuplicateCount?: number
  hasInvoices?: boolean
}

export interface IWireDataAggregation {
  totalCount: number
  totalAmount: number
  mappedCount: number
  data: IWireData[]
}

export interface IWireTotals {
  mapping: number
  totalAmount: number
  totalWires?: number
  wiresPostingToday?: number
  wiresPostedYesterday?: number
  checksPostedYesterday?: number
  reconciliationAmount: number
}

export interface IWiresDataReconciliationAccounts {
  bank: number
  operating: number
  concentration: number
  discrepancy: number
}

export interface IAggregationDataRow {
  id: string
  clientName: string
  debtor: string
  recordDate: string
  processedAt: string
  boxLink?: string
  accountNumber?: string
  description: string
  amount: number
  paymentType: CollectionType
  isEditable: boolean
  entityId: string
  clientId: string
  routingNumber?: string
  totalCount?: number
  hasInvoices?: boolean
}

interface IAggregationDataTopRow {
  clientId: string
  clientName: string
  clientStatus: ClientInfoStatus
  checksPaymentAmount: number
  checksCount: number
  wiresPaymentAmount: number
  wiresCount: number
  totalPaymentAmount: number
  rows?: IAggregationDataRow[]
}
export interface IAggregationData {
  data: IAggregationDataTopRow[]
  totals: {
    checksPaymentSum: number
    wiresPaymentSum: number
    totalChecksCount: number
    totalWiresCount: number
    totalItems: number
  }
}

export enum COLLECTIONS_TABS {
  OVERALL_COLLECTIONS = 'Overall collections',
  NON_CLIENT_RELATED_CASH = 'Non-client related cash',
}

export interface IMasterCollection {
  id: string
  clientName: string
  clientInfo?: IClientInfo
  clientId?: string
  clientIconUrl?: string
  recordDate: string
  paymentAmount: number
  debtor: string
  entityId: string
  paymentType: string
  boxLink: string
  checkNumber?: string
}

export const IDB_LOAN_ACCOUNT_NUMBER = '1000003294'

export enum RefreshType {
  SkipLoader = 'skipLoader',
  Refresh = 'refresh',
}

export interface ICheckAliasData {
  data: ICheckAlias
}

export interface ICheckAlias {
  id: string
  clientName: string
  aliases: string[]
}

export interface ICollectionInvoice {
  masterInvoiceId: string | null
  invoiceNumber: string
  debtor: string | null
  entityId: string | null
  entityName: string | null
  invoiceDate: string | null
  paidDate: string
  invoiceAmount: number | null
  paidAmount: number | null
  firstSeenBbc: string | null
  lastSeenBbc: string | null
  canOpenModal: boolean
}
